import React, { Component } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Grid, Card, Image, Divider, List, Table, Button, Icon, Loader, Header, Popup } from 'semantic-ui-react';
import { countFormat } from '../../utils/helpers/formatting';
import ConfirmationQuestion from '../../components/confirmation-question';
import TargetLink from '../../components/target-link';

const ANALYTICS_USERS = [
  'd9013d29-506a-4ee7-b178-652fab37318e',
  '518f7135-6720-4e72-ae6a-23ad7e9074b2',
  '8fed9f11-f240-4101-b558-47deaf28a422',
  '31557883-9d94-40d6-aadf-a6b2acfcc349',
  'b5ea5d47-8e4a-445a-9c17-8f0a69bfa90d',
];

const gridStyle = {
  marginTop: 25,
  textAlign: 'center',
};

const imageStyle = {
  margin: '0 auto',
  display: 'block',
  boxShadow: '0 0 10px #777',
};

const nameStyle = {
  padding: 10,
};

const descriptionStyle = {
  color: '#999',
};

const cardStyle = {
  paddingTop: 25,
  minHeight: 530,
};

const platformColors = {
  facebook: 'blue',
  youtube: 'red',
  twitter: 'blue',
  instagram: 'pink',
};

const errorStyle = {
  paddingTop: 170,
  color: 'gray',
};

const SCOPES = {
  facebook: [
    'manage_pages',
    'instagram_manage_insights',
    'instagram_basic',
    'read_insights',
  ],
};

const COMMUNITY_PAGE_HELP_LINK = 'http://help.dyzio.co/en/articles/4063438-how-to-create-a-facebook-community-page-and-connect-it-to-instagram';
const LINK_ACCOUNT_HELP_LINK = 'http://help.dyzio.co/en/articles/4044324-how-to-link-your-instagram-account-to-your-facebook-fan-page';


const CONFIRMATION_STATES = {
  NO_CONFIRMATION_NEEDED: 'NO_CONFIRMATION_NEEDED',
  FAN_PAGE_UNKNOWN: 'FAN_PAGE_UNKNOWN',
  FAN_PAGE_UNCONFIRMED: 'FAN_PAGE_UNCONFIRMED',
  ACCOUNT_CONNECTED_UNKNOWN: 'ACCOUNT_CONNECTED_UNKNOWN',
  ACCOUNT_CONNECTED_UNCONFIRMED: 'ACCOUNT_CONNECTED_UNCONFIRMED',
  READY_TO_BE_SENT_TO_FACEBOOK: 'READY_TO_BE_SENT_TO_FACEBOOK',
};

export const getFacebookAuthorisationLink = ({ platform, id, callbackUrl }) => {
  const state = `{"influencerId":"${id}","callbackURL":"${callbackUrl}","platform":"${platform}"}`;
  const encodedState = encodeURIComponent(state);
  const BASE_URL = 'https://www.facebook.com/v3.3/dialog/oauth';

  const fields = {
    client_id: 307083449705342,
    response_type: 'code',
    redirect_uri: 'https://uiqcrs8dhb.execute-api.eu-west-1.amazonaws.com/production/callback/facebook',
    scope: _.join(SCOPES.facebook, ','),
    auth_type: 'rerequest',
    state: encodedState,
  };

  return `${BASE_URL}?client_id=${fields.client_id}&response_type=${fields.response_type}&redirect_uri=${fields.redirect_uri}&scope=${fields.scope}&auth_type=rerequest&state=${encodedState}`;
};

const authorisePlatform = ({ platform, id }) => {
  const host = _.get(window, 'origin', 'https://app.dyzio.co');
  switch (platform) {
  case 'facebook':
  case 'instagram':
    window.location.href = getFacebookAuthorisationLink({ platform, id, callbackUrl: `${host}/token-thanks` });
    break;
  default: break;
  }
};

class InfluencerAuthorisationCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      confirmationState: CONFIRMATION_STATES.NO_CONFIRMATION_NEEDED,
    };
  }

  onAuthoriseButtonClick = ({
    platform,
    id,
  }) => {
    const shouldConfirm = _.includes(['facebook', 'instagram'], platform);
    if (!shouldConfirm) {
      authorisePlatform({ platform, id });
    } else {
      this.setState({
        confirmationState: CONFIRMATION_STATES.FAN_PAGE_UNKNOWN,
        platform,
        id,
      });
    }
  }

  handleConfirmation = () => {
    const { platform, id } = this.state;
    authorisePlatform({ platform, id });
  }

  authoriseAnalytics = () => {
    const {
      id: influencerId,
    } = this.props;

    const { origin } = window.location;
    const callbackURL = `${origin}/token-thanks`;
    axios.post('https://uiqcrs8dhb.execute-api.eu-west-1.amazonaws.com/production/oauth/analytics/url', {
      data: {
        influencerId,
        callbackURL,
      },
    }).then(r => {
      window.location.href = r.data.headers.Location;
    });
  }

  authoriseYoutube = influencerId => {
    const host = _.get(window, 'origin', 'https://app.dyzio.co');

    axios.post('https://uiqcrs8dhb.execute-api.eu-west-1.amazonaws.com/production/oauth/youtube/url', {
      data: {
        influencerId,
        callbackURL: `${host}/token-thanks`,
      },
    }).then(r => {
      window.location.href = r.data.headers.Location;
    });
  }

  render() {
    const {
      id: influencerId,
      name,
      primarySocialHandle,
      thumbnail,
      youtube,
      facebook,
      instagram,
      twitter,
      facebookCount,
      twitterCount,
      instagramCount,
      youtubeCount,
      isLoading,
      isFacebookPersonalPage,
      isError,
    } = this.props;

    const { confirmationState } = this.state;

    const shouldRenderChannelsList = _.eq(CONFIRMATION_STATES.NO_CONFIRMATION_NEEDED, confirmationState);

    const shouldRenderFanPageConfirmation = _.includes([
      CONFIRMATION_STATES.FAN_PAGE_UNKNOWN,
      CONFIRMATION_STATES.FAN_PAGE_UNCONFIRMED,
    ], confirmationState);

    const shouldRenderAccountConnected = _.includes([
      CONFIRMATION_STATES.ACCOUNT_CONNECTED_UNKNOWN,
      CONFIRMATION_STATES.ACCOUNT_CONNECTED_UNCONFIRMED,
    ], confirmationState);

    const shouldRenderFacebookMessage = _.includes([
      CONFIRMATION_STATES.READY_TO_BE_SENT_TO_FACEBOOK,
    ], confirmationState);

    const isFanPageNotConfirmed = _.eq(CONFIRMATION_STATES.FAN_PAGE_UNCONFIRMED, confirmationState);
    const isAccountNotConnected = _.eq(CONFIRMATION_STATES.ACCOUNT_CONNECTED_UNCONFIRMED, confirmationState);

    const channels = [
      { handle: facebook, platform: 'facebook', count: facebookCount, color: 'blue', id: influencerId },
      { handle: twitter, platform: 'twitter', count: twitterCount, color: 'blue', id: influencerId },
      { handle: instagram, platform: 'instagram', count: instagramCount, color: 'purple', id: influencerId },
      { handle: youtube, platform: 'youtube', count: youtubeCount, color: 'red', id: influencerId },
    ];


    const isAnalyticsUser = _.includes(ANALYTICS_USERS, influencerId);

    return (
      <Grid container style={gridStyle}>
        <Grid.Row>
          <Grid.Column computer={5} only="computer" />
          <Grid.Column computer={6} mobile={16}>
            <Card fluid style={cardStyle}>
              {
                isLoading && <Loader active content="Loading..." />
              }
              {
                isError && (
                  <Header as="h4" icon style={errorStyle}>
                    <Icon name="frown" />
                  Something went wrong. Please try again later.
                  </Header>
                )
              }
              {
                !isLoading && !isError && (
                  <Card.Content>
                    <Image
                      style={imageStyle}
                      className="circular"
                      size="small"
                      src={thumbnail}
                    />
                    <h3 style={nameStyle}>{name}</h3>
                    <p style={descriptionStyle}>{primarySocialHandle || '-'}</p>
                    <Divider />

                    {
                      shouldRenderFacebookMessage &&
                      <ConfirmationQuestion
                        onReset={() => this.handleConfirmation()}
                        onPositive={_.noop}
                        onNegative={_.noop}
                        question=""
                        explanationButtonText="Authorise"
                        shouldRenderExplanation
                        explanation={
                          <div>
                            <p>You will now be sent to Facebook to authorise access to your account. </p>
                            <Divider hidden />
                            <p>The access is readonly. Please do not remove any of the permissions or it will not work.</p>
                          </div>
                        }
                      />
                    }

                    {
                      shouldRenderAccountConnected &&
                      <ConfirmationQuestion
                        onNegative={() => this.setState({
                          confirmationState: CONFIRMATION_STATES.ACCOUNT_CONNECTED_UNCONFIRMED,
                        })}
                        onReset={() => this.setState({
                          confirmationState: CONFIRMATION_STATES.ACCOUNT_CONNECTED_UNKNOWN,
                        })}
                        onPositive={() => this.setState({
                          confirmationState: CONFIRMATION_STATES.READY_TO_BE_SENT_TO_FACEBOOK,
                        })}
                        question={
                          <span>Is your Instagram account<br /> linked to it?</span>
                        }
                        explanation={
                          <div>
                            <p>Your Instagram account must be linked to your Community Page.</p>
                            <br />
                            <p>Click <TargetLink renderIf href={LINK_ACCOUNT_HELP_LINK}>here</TargetLink> to find out more.</p>
                          </div>
                        }
                        shouldRenderExplanation={isAccountNotConnected}
                      />
                    }

                    {
                      shouldRenderFanPageConfirmation &&
                      <ConfirmationQuestion
                        onNegative={() => this.setState({
                          confirmationState: CONFIRMATION_STATES.FAN_PAGE_UNCONFIRMED,
                        })}
                        onReset={() => this.setState({
                          confirmationState: CONFIRMATION_STATES.FAN_PAGE_UNKNOWN,
                        })}
                        onPositive={() => this.setState({
                          confirmationState: CONFIRMATION_STATES.ACCOUNT_CONNECTED_UNKNOWN,
                        })}
                        question={
                          <span>Do you have a <br />Facebook Community Page?</span>
                        }
                        explanation={
                          <div>
                            <p>You must have a Facebook Community page to authorise the system to fetch your Instagram Insights data.</p>
                            <br />
                            <p>Use this <TargetLink href={COMMUNITY_PAGE_HELP_LINK} renderIf>link</TargetLink> to find out how to set one up. <br />It{'\''}s a good thing!</p>
                          </div>
                        }
                        shouldRenderExplanation={isFanPageNotConfirmed}
                      />
                    }

                    {
                      shouldRenderChannelsList &&
                      <div>
                        <Table singleLine unstackable>
                          <Table.Body>
                            {
                              _.map(channels, ({ handle, platform, count }) => (
                                <Table.Row key={platform}>
                                  <Table.Cell>
                                    <Icon name={platform} color={platformColors[platform]} />
                                  </Table.Cell>
                                  <Table.Cell>
                                    {handle}
                                  </Table.Cell>
                                  <Table.Cell textAlign="center">
                                    {countFormat(count)}
                                  </Table.Cell>
                                  {
                                    isFacebookPersonalPage &&
                                    <Table.Cell textAlign="right">
                                      {
                                        platform === 'facebook' &&
                                        <Popup
                                          content="This is a personal page. Your instagram account must be linked to a Community page."
                                          trigger={
                                            <Icon
                                              size="small"
                                              className="circular"
                                              color="red"
                                              name="warning"
                                              inverted
                                            />
                                          }
                                        />
                                      }
                                    </Table.Cell>
                                  }
                                </Table.Row>
                              ))
                            }
                          </Table.Body>
                        </Table>
                        <Divider />
                        <List verticalAlign="middle" >
                          {

                            _.map(_.filter(channels, ['platform', 'instagram']), ({ platform, id, color }) => (
                              <List.Item key={`${platform}-${id}`}>
                                <Button
                                  fluid
                                  icon
                                  onClick={() => this.onAuthoriseButtonClick({ platform, id })}
                                  color={color}
                                  labelPosition="left"
                                >
                                  <Icon name={platform} /> Authorise {_.upperFirst(platform)} (readonly)
                                </Button>
                              </List.Item>
                            ))
                          }

                          {
                            youtube &&
                            <div>
                              <hr />
                              <Button
                                fluid
                                icon
                                onClick={() => this.authoriseYoutube(influencerId)}
                                color="red"
                                labelPosition="left"
                              >
                                <Icon name="youtube" /> Authorise YouTube (readonly)
                              </Button>
                            </div>
                          }

                          {
                            isAnalyticsUser &&
                            <div>
                              <hr />
                              <Button
                                fluid
                                icon
                                onClick={this.authoriseAnalytics}
                                color="red"
                                labelPosition="left"
                              >
                                <Icon name="google" /> Authorise Google Analytics (readonly)
                              </Button>
                            </div>
                          }
                        </List>
                      </div>
                    }


                  </Card.Content>
                )
              }
            </Card>
          </Grid.Column>
          <Grid.Column computer={5} only="computer" />
        </Grid.Row>
      </Grid>
    );
  }
}

InfluencerAuthorisationCard.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  primarySocialHandle: PropTypes.string.isRequired,
  thumbnail: PropTypes.string.isRequired,
  youtube: PropTypes.string.isRequired,
  facebook: PropTypes.string.isRequired,
  instagram: PropTypes.string.isRequired,
  twitter: PropTypes.string.isRequired,
  facebookCount: PropTypes.string.isRequired,
  twitterCount: PropTypes.string.isRequired,
  instagramCount: PropTypes.string.isRequired,
  youtubeCount: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  isFacebookPersonalPage: PropTypes.bool,
};

InfluencerAuthorisationCard.defaultProps = {
  isFacebookPersonalPage: false,
};

export default InfluencerAuthorisationCard;



/** WEBPACK FOOTER **
 ** ./src/components/influencer-authorisation-card/index.js
 **/